import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Loader2, Eye, EyeOff, Mail, CheckCircle, RotateCcw, KeyRound, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Module-level guards survive React StrictMode's unmount→remount cycle.
// useRef resets on remount, so we use these instead.
const _processedSessions = new Set();
let _loginSucceeded = false;

// AuthCallback component handles OAuth redirect
export const AuthCallback = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();

  // Navigate only AFTER React has committed the user state update.
  // This prevents the ProtectedRoute race condition (user=null at navigate time).
  useEffect(() => {
    if (user && _loginSucceeded) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const hash = window.location.hash;
    const params = new URLSearchParams(hash.replace("#", "?"));
    const sessionId = params.get("session_id");

    if (!sessionId) {
      navigate("/auth?error=oauth_failed");
      return;
    }

    // StrictMode mounts twice with the same session_id — skip the duplicate.
    if (_processedSessions.has(sessionId)) return;
    _processedSessions.add(sessionId);
    _loginSucceeded = false; // reset for this new OAuth session

    axios.post(`${API}/auth/google/callback`, { session_id: sessionId }, { withCredentials: true })
      .then(res => {
        _loginSucceeded = true;
        login(res.data.user, res.data.token);
        // Navigation is handled by the user-watching useEffect above
      })
      .catch(() => {
        // Allow retry: remove so a fresh attempt can proceed
        _processedSessions.delete(sessionId);
        navigate("/auth?error=oauth_failed");
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, user } = useAuth();

  const searchParams = new URLSearchParams(location.search);
  const defaultTab = searchParams.get("tab") === "register" ? "register" : "login";
  const oauthError = searchParams.get("error");

  const [tab, setTab] = useState(defaultTab);
  const [error, setError] = useState(oauthError ? "OAuth sign in failed. Please try again." : "");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registered, setRegistered] = useState(null); // {email} after successful registration
  const [unverifiedEmail, setUnverifiedEmail] = useState(""); // email blocked at login
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [forgotFlow, setForgotFlow] = useState(null); // null | "form" | "sent"
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const [loginForm, setLoginForm]   = useState({ email: "", password: "" });
  const [regForm, setRegForm]       = useState({ name: "", email: "", password: "" });

  // Capture referral code from URL (?ref=WORKPRO-XXXX)
  const refCode = searchParams.get("ref") || "";

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user]);

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setUnverifiedEmail("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      login(res.data.user, res.data.token);
      // Navigation handled by useEffect watching user state to avoid race condition
    } catch (err) {
      if (err.response?.data?.detail === "email_not_verified") {
        setUnverifiedEmail(loginForm.email);
      } else {
        setError(err.response?.data?.detail || "Login failed. Please check your credentials.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (!regForm.name.trim()) { setError("Please enter your name."); return; }
    setLoading(true);
    try {
      const payload = { ...regForm, ...(refCode ? { referral_code: refCode } : {}) };
      const res = await axios.post(`${API}/auth/register`, payload, { withCredentials: true });
      if (res.data.status === "pending_verification") {
        setRegistered({ email: regForm.email });
      } else {
        login(res.data.user, res.data.token);
        // Navigation handled by useEffect watching user state to avoid race condition
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { setForgotError("Please enter your email address."); return; }
    setForgotError("");
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotFlow("sent");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "email_service_not_configured") {
        setForgotError("Email service is not configured yet. Please contact support.");
      } else {
        setForgotFlow("sent"); // Still show success to avoid enumeration
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResendVerification = async (email) => {
    setResendLoading(true);
    setResendSent(false);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResendSent(true);
    } catch {
      setResendSent(true); // show success regardless to avoid enumeration
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel */}
      <div className="hidden lg:flex w-1/2 gradient-hero flex-col justify-center px-16">
        <div className="max-w-md">
          <div className="mb-10">
            <div style={{width:'160px', height:'42px', overflow:'hidden', position:'relative'}}>
              <img
                src="https://customer-assets.emergentagent.com/job_workpro-ai/artifacts/aayaszwu_imresizer-Promptlee.png"
                alt="Promptlee"
                style={{width:'215px', position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)'}}
              />
            </div>
          </div>
          <h2 className="text-3xl font-bold text-slate-900 mb-4 leading-tight">
            AI Applications for Every Business Challenge
          </h2>
          <p className="text-slate-600 mb-8 leading-relaxed">
            Access structured AI prompts for Procurement, Finance, Marketing, HR, Sales, and Operations.
          </p>
          <div className="space-y-3">
            {["17+ ready-to-use AI applications", "7 business functions covered", "Free plan available"].map(item => (
              <div key={item} className="flex items-center gap-2 text-sm text-slate-700">
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 text-xs">✓</span>
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center justify-center mb-8">
            <div style={{width:'160px', height:'42px', overflow:'hidden', position:'relative'}}>
              <img
                src="https://customer-assets.emergentagent.com/job_workpro-ai/artifacts/aayaszwu_imresizer-Promptlee.png"
                alt="Promptlee"
                style={{width:'215px', position:'absolute', top:'50%', left:'50%', transform:'translate(-50%, -50%)'}}
              />
            </div>
          </div>

          {/* Forgot Password — form state */}
          {forgotFlow === "form" ? (
            <div className="py-2" data-testid="forgot-password-panel">
              <button
                onClick={() => { setForgotFlow(null); setForgotEmail(""); setForgotError(""); }}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6 transition-colors"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft size={14} /> Back to sign in
              </button>
              <div className="w-14 h-14 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center mb-5">
                <KeyRound size={24} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Forgot your password?</h2>
              <p className="text-sm text-slate-600 mb-6">
                Enter your email and we'll send you a reset link.
              </p>
              {forgotError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{forgotError}</AlertDescription>
                </Alert>
              )}
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email">Email address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@company.com"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className="mt-1"
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={forgotLoading}
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? <Loader2 className="animate-spin mr-2 w-4 h-4" /> : null}
                  Send reset link
                </Button>
              </form>
            </div>

          /* Forgot Password — sent state */
          ) : forgotFlow === "sent" ? (
            <div className="text-center py-4" data-testid="forgot-sent-panel">
              <div className="w-16 h-16 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Mail size={28} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Check your inbox</h2>
              <p className="text-sm text-slate-600 mb-1">
                If an account exists for
              </p>
              <p className="text-sm font-semibold text-slate-900 mb-4">{forgotEmail}</p>
              <p className="text-sm text-slate-600 mb-8">
                you'll receive a password reset link shortly. It expires in 1 hour.
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="w-full text-slate-500"
                onClick={() => { setForgotFlow(null); setForgotEmail(""); setForgotError(""); }}
                data-testid="back-to-signin-btn"
              >
                Back to sign in
              </Button>
            </div>

          /* Registration success — "Check your inbox" state */
          ) : registered ? (
            <div className="text-center py-4" data-testid="registration-success-panel">
              <div className="w-16 h-16 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Mail size={28} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Check your inbox</h2>
              <p className="text-sm text-slate-600 mb-1">
                We sent a verification link to
              </p>
              <p className="text-sm font-semibold text-slate-900 mb-6">{registered.email}</p>
              <p className="text-xs text-slate-500 mb-8">
                Click the link in the email to activate your account. It expires in 24 hours.
              </p>
              <div className="space-y-3">
                {!resendSent ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-slate-600"
                    onClick={() => handleResendVerification(registered.email)}
                    disabled={resendLoading}
                    data-testid="resend-verification-btn"
                  >
                    {resendLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <RotateCcw size={14} className="mr-2" />}
                    Resend verification email
                  </Button>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-emerald-600 text-sm">
                    <CheckCircle size={15} /> Email resent successfully
                  </div>
                )}
                <Button variant="ghost" size="sm" className="w-full text-slate-500" onClick={() => { setRegistered(null); setTab("login"); }}>
                  Back to sign in
                </Button>
              </div>
            </div>

          /* Unverified email blocked at login state */
          ) : unverifiedEmail ? (
            <div className="text-center py-4" data-testid="unverified-email-panel">
              <div className="w-16 h-16 bg-amber-50 border border-amber-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <Mail size={28} className="text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Verify your email first</h2>
              <p className="text-sm text-slate-600 mb-1">
                Your account is not yet verified. Check your inbox for a verification link sent to:
              </p>
              <p className="text-sm font-semibold text-slate-900 mb-6">{unverifiedEmail}</p>
              <div className="space-y-3">
                {!resendSent ? (
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => handleResendVerification(unverifiedEmail)}
                    disabled={resendLoading}
                    data-testid="resend-verification-btn"
                  >
                    {resendLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <RotateCcw size={14} className="mr-2" />}
                    Resend verification email
                  </Button>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-emerald-600 text-sm py-2">
                    <CheckCircle size={15} /> Verification email sent — check your inbox
                  </div>
                )}
                <Button variant="ghost" size="sm" className="w-full text-slate-500" onClick={() => { setUnverifiedEmail(""); setResendSent(false); }}>
                  Back to sign in
                </Button>
              </div>
            </div>

          ) : (
            <>
            <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login">Sign In</TabsTrigger>
              <TabsTrigger value="register">Create Account</TabsTrigger>
            </TabsList>

            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Social Login Buttons */}
            <div className="space-y-2 mb-4">
              <Button
                variant="outline"
                className="w-full flex items-center gap-3 border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={handleGoogleLogin}
                data-testid="google-login-btn"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </Button>

              <Button
                variant="outline"
                className="w-full flex items-center gap-3 border-slate-300 text-slate-500 cursor-not-allowed opacity-60"
                disabled
                data-testid="linkedin-login-btn"
                title="LinkedIn login coming soon"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="#0A66C2">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn (Coming Soon)
              </Button>
            </div>

            <div className="relative mb-4">
              <Separator />
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-50 px-2 text-xs text-slate-500">
                or continue with email
              </span>
            </div>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@company.com"
                    value={loginForm.email}
                    onChange={e => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={e => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => { setForgotFlow("form"); setForgotEmail(loginForm.email); setError(""); }}
                    className="text-xs text-blue-600 hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? <Loader2 className="animate-spin mr-2 w-4 h-4" /> : null}
                  Sign In
                </Button>
              </form>
              <p className="mt-4 text-center text-sm text-slate-600">
                Don't have an account?{" "}
                <button onClick={() => setTab("register")} className="text-blue-600 font-medium hover:underline">
                  Create one free
                </button>
              </p>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                {refCode && (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2.5 text-sm" data-testid="referral-banner">
                    <span className="text-emerald-500 font-bold">+</span>
                    You were invited! You'll start with bonus credits after signing up.
                  </div>
                )}
                <div>
                  <Label htmlFor="reg-name">Full Name</Label>
                  <Input
                    id="reg-name"
                    placeholder="Jane Smith"
                    value={regForm.name}
                    onChange={e => setRegForm({ ...regForm, name: e.target.value })}
                    required
                    data-testid="register-name-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-email">Work Email</Label>
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="you@company.com"
                    value={regForm.email}
                    onChange={e => setRegForm({ ...regForm, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="reg-password">Password</Label>
                  <div className="relative mt-1">
                    <Input
                      id="reg-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 6 characters"
                      value={regForm.password}
                      onChange={e => setRegForm({ ...regForm, password: e.target.value })}
                      required
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? <Loader2 className="animate-spin mr-2 w-4 h-4" /> : null}
                  Create Free Account
                </Button>
              </form>
              <p className="mt-4 text-center text-sm text-slate-500">
                By signing up, you agree to our Terms of Service and Privacy Policy.
              </p>
              <p className="mt-2 text-center text-sm text-slate-600">
                Already have an account?{" "}
                <button onClick={() => setTab("login")} className="text-blue-600 font-medium hover:underline">
                  Sign in
                </button>
              </p>
            </TabsContent>
          </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
